import React from 'react';
import { BrowserRouter as Router, Route, Routes } from 'react-router-dom';
import { Header, Footer, ScrollToTop, ToastProvider, BottomNavigation } from '@/components';
import FeedbackButton from '@/components/FeedbackButton';
import { Chrome } from '@/components/layout';
import { AuthProvider } from '@/contexts/AuthContext';
import { CapabilitiesProvider } from '@/contexts/CapabilitiesContext';
import { LayoutProvider, useLayout } from '@/contexts/LayoutContext';
import { PlansProvider } from '@/contexts/PlansContext';
import { PendingJobsProvider } from '@/contexts/PendingJobsContext';
import { AddProvider } from '@/contexts/AddContext';
import Home from '@/pages/home/Home';
import FreeTrial from '@/pages/free-trial/FreeTrial';
import Product from '@/pages/product/Product';
import Templates from '@/pages/templates/Templates';
import AddPage from '@/pages/add/Add';
import LoginPage from '@/pages/auth/LoginPage';
import SignupPage from '@/pages/auth/SignupPage';
import SignupSuccess from '@/pages/auth/signup/SignupSuccess';
import ForgotPasswordPage from '@/pages/auth/ForgotPasswordPage';
import ResendVerificationPage from '@/pages/auth/ResendVerificationPage';
import ArchitectureBrowser from '@/pages/architecture/ArchitectureBrowser';
import DocumentImport from '@/pages/architecture/DocumentImport';
import DocsLanding from '@/pages/projects/Projects';
import ProjectDashboard from '@/pages/dashboard/ProjectDashboard';

import About from '@/pages/about/About';
import TermsOfService from '@/pages/legal/TermsOfService';
import PrivacyPolicy from '@/pages/legal/PrivacyPolicy';
import InformationRetentionPolicy from '@/pages/legal/InformationRetentionPolicy';
import { initSentry } from '@/lib/sentry';
import ContactPage from '@/pages/free-trial/ContactPage';
import AdminDashboard from '@/pages/admin/AdminDashboard';
import AdminTemplates from '@/pages/admin/AdminTemplates';
import AdminSystemPrompts from '@/pages/admin/AdminSystemPrompts';
import AdminPlans from '@/pages/admin/AdminPlans';
import AdminOperatingModel from '@/pages/admin/AdminOperatingModel';
import UpgradePage from '@/pages/upgrade/UpgradePage';
import PlanPage from '@/pages/plan/PlanPage';
import AccountSettings from '@/pages/account/AccountSettings';
import NotFound from '@/pages/NotFound';

// Initialize Sentry before rendering
initSentry();

const AppContent: React.FC = () => {
  const { styles } = useLayout();

  return (
    <PendingJobsProvider>
      <AddProvider>
        <ScrollToTop />
        <div className="flex flex-col min-h-screen">
          <Chrome component="header">
            <Header />
          </Chrome>
          <main className={`flex-grow ${styles.mainPadding ? 'pb-16' : ''}`}>
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/product" element={<Product />} />
              <Route path="/project-settings" element={<Templates />} />

              <Route path="/projects" element={<DocsLanding />} />
              <Route path="/insights" element={<ProjectDashboard />} />
              <Route path="/contact" element={<ContactPage />} />
              <Route path="/free-trial" element={<FreeTrial />} />
              <Route path="/signup" element={<SignupPage />} />
              <Route path="/signup/success" element={<SignupSuccess />} />
              <Route path="/login" element={<LoginPage />} />
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
              <Route path="/resend-verification" element={<ResendVerificationPage />} />
              <Route path="/add" element={<AddPage />} />
              <Route path="/architecture" element={<ArchitectureBrowser />} />
              <Route path="/architecture/import" element={<DocumentImport />} />
              <Route path="/about" element={<About />} />
              <Route path="/terms" element={<TermsOfService />} />
              <Route path="/privacy" element={<PrivacyPolicy />} />
              <Route path="/retention" element={<InformationRetentionPolicy />} />
              <Route path="/admin" element={<AdminDashboard />} />
              <Route path="/admin/templates" element={<AdminTemplates />} />
              <Route path="/admin/system-prompts" element={<AdminSystemPrompts />} />
              <Route path="/admin/plans" element={<AdminPlans />} />
              <Route path="/admin/operating-model" element={<AdminOperatingModel />} />
              <Route path="/upgrade" element={<UpgradePage />} />
              <Route path="/plan" element={<PlanPage />} />
              <Route path="/account" element={<AccountSettings />} />
              <Route path="*" element={<NotFound />} />
            </Routes>
          </main>
          <Chrome component="footer">
            <Footer />
          </Chrome>
          <Chrome component="feedbackButton">
            <FeedbackButton />
          </Chrome>
          <Chrome component="bottomNav">
            <BottomNavigation />
          </Chrome>
        </div>
      </AddProvider>
    </PendingJobsProvider>
  );
};

const App: React.FC = () => {
  return (
    <PlansProvider>
      <ToastProvider>
        <AuthProvider>
          <CapabilitiesProvider>
            <Router>
              <LayoutProvider>
                <AppContent />
              </LayoutProvider>
            </Router>
          </CapabilitiesProvider>
        </AuthProvider>
      </ToastProvider>
    </PlansProvider>
  );
}

export default App;